import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  AlertCircle,
  Check,
  CheckCircle2,
  Copy,
  ListVideo,
  Loader2,
  Plus,
} from 'lucide-react';
import { createPageUrl } from '@/utils';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';

/**
 * 受付完了画面。
 * 変換完了後の登録はサーバー側（EventBridge）が行うため、この画面を閉じてよい。
 * 共有用URLは動画識別子から組み立てているので、変換を待たずに表示できる。
 */
export default function AdminVideoCreateAccepted({
  result,
  phase,
  transcodeProgress,
  errorMessage,
  pollErrorMessage,
  onCreateAnother,
}) {
  const [copied, setCopied] = useState(false);
  const [copyFailed, setCopyFailed] = useState(false);
  const shareUrl = result?.shareUrl ?? null;
  const isCompleted = phase === 'completed';
  const isFailed = phase === 'failed';

  const handleCopy = async () => {
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopyFailed(false);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2500);
    } catch {
      // クリップボードが使えない環境向け。URLは画面に出しているので手動選択で拾える。
      setCopied(false);
      setCopyFailed(true);
    }
  };

  return (
    <div className="space-y-4">
      <div className="rounded-2xl border border-[#e8dccd] bg-white p-6">
        <div className="text-center">
          <div
            className={`mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl ${
              isFailed ? 'bg-[#b4462f]/15' : 'bg-[#6b8f5d]/15'
            }`}
          >
            {isFailed ? (
              <AlertCircle className="h-8 w-8 text-[#b4462f]" />
            ) : (
              <CheckCircle2 className="h-8 w-8 text-[#6b8f5d]" />
            )}
          </div>
          <h2 className="text-lg font-bold text-[#2d1810]">
            {isFailed
              ? '登録に失敗しました'
              : isCompleted
                ? '登録が完了しました'
                : 'アップロードを受け付けました'}
          </h2>
          <p className="mt-1 text-sm text-[#6b5540]">
            {isFailed
              ? '動画は登録されていません。一時ファイルと変換結果はサーバー側で削除されます。'
              : isCompleted
                ? '動画がアーカイブサイトに追加されました。共有用URLはすぐに使えます。'
                : 'このあとの変換とサイトへの登録はサーバー側で自動的に行われます。この画面は閉じて大丈夫です。'}
          </p>
        </div>

        <div className="mt-5 space-y-3">
          <div className="rounded-xl bg-[#faf6f1] px-4 py-3">
            <p className="text-xs font-medium text-[#8b6f47]">動画タイトル</p>
            <p className="mt-1 break-words text-sm font-medium text-[#2d1810]">
              {result?.title || '(タイトル未設定)'}
            </p>
          </div>

          <div className="rounded-xl bg-[#faf6f1] px-4 py-3">
            <p className="text-xs font-medium text-[#8b6f47]">動画識別子</p>
            <p className="mt-1 text-sm font-medium text-[#2d1810]">
              <code>{result?.publicId ?? '-'}</code>
              {result?.videoId != null && (
                <span className="ml-2 text-xs text-[#6b5540]">video ID: {result.videoId}</span>
              )}
            </p>
          </div>

          {!isFailed && (
            <div className="rounded-xl bg-[#faf6f1] px-4 py-3">
              <p className="text-xs font-medium text-[#8b6f47]">共有用URL</p>
              {shareUrl ? (
                <>
                  <div className="mt-2 flex flex-col gap-2 sm:flex-row sm:items-center">
                    <code className="min-w-0 flex-1 break-all rounded-lg border border-[#e8dccd] bg-white px-3 py-2 text-xs text-[#2d1810]">
                      {shareUrl}
                    </code>
                    <Button
                      type="button"
                      onClick={handleCopy}
                      className="gap-2 bg-[#8b6f47] text-[#f5ede3] hover:bg-[#6b5540] sm:w-36"
                    >
                      {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                      {copied ? 'コピーしました' : 'URLをコピー'}
                    </Button>
                  </div>
                  {copyFailed && (
                    <p className="mt-2 text-xs text-[#b4462f]">
                      コピーに失敗しました。上のURLを選択してコピーしてください。
                    </p>
                  )}
                  {!isCompleted && (
                    <p className="mt-2 text-xs text-[#b4462f]">
                      変換が完了するまでこのURLは開けません。共有するのは完了通知が届いたあとにしてください。
                    </p>
                  )}
                </>
              ) : (
                <p className="mt-1 text-sm text-[#b4462f]">
                  共有用URLを生成できませんでした（動画識別子が取得できていません）。
                  登録状況は「動画一覧で確認する」から確認してください。
                </p>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="rounded-2xl border border-[#e8dccd] bg-white p-5">
        <div className="mb-3 flex items-center gap-2">
          {isFailed ? (
            <AlertCircle className="h-4 w-4 text-[#b4462f]" />
          ) : isCompleted ? (
            <CheckCircle2 className="h-4 w-4 text-[#6b8f5d]" />
          ) : (
            <Loader2 className="h-4 w-4 animate-spin text-[#8b6f47]" />
          )}
          <h3 className="text-sm font-semibold text-[#2d1810]">
            {isFailed ? '処理結果' : isCompleted ? '変換・登録の状況' : '変換の状況'}
          </h3>
        </div>

        {isFailed ? (
          <p className="break-words text-sm text-[#b4462f]">{errorMessage}</p>
        ) : (
          <>
            <Progress
              value={isCompleted ? 100 : Math.round(Number(transcodeProgress) || 0)}
              className="h-2 bg-[#efe5d8]"
            />
            <p className="mt-2 text-xs text-[#6b5540]">
              {isCompleted
                ? 'MP4への変換とvideosテーブルへの登録が完了しました。'
                : `MP4への変換中（${Math.round(Number(transcodeProgress) || 0)}%）。動画の長さの2〜5倍程度かかります。`}
            </p>
            {!isCompleted && (
              <p className="mt-1 text-xs text-[#8b6f47]">
                この画面を開いている間は5秒ごとに状況を更新します。閉じた場合も、完了後にサーバー側で登録され、設定されていればDiscordへ通知が飛びます。
              </p>
            )}
            {pollErrorMessage && (
              <p className="mt-1 text-xs text-[#8b6f47]">
                状況の取得に失敗しました（{pollErrorMessage}）。処理自体は続いています。
              </p>
            )}
          </>
        )}
      </div>

      <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
        <Button
          asChild
          variant="outline"
          className="gap-2 border-[#d4c4b0] text-[#5d4529] hover:bg-[#f5ede3]"
        >
          <Link to={createPageUrl('AdminVideoIndex')}>
            <ListVideo className="h-4 w-4" />
            動画一覧で確認する
          </Link>
        </Button>
        <Button
          type="button"
          onClick={onCreateAnother}
          className="gap-2 bg-[#8b6f47] text-[#f5ede3] hover:bg-[#6b5540]"
        >
          <Plus className="h-4 w-4" />
          続けて追加する
        </Button>
      </div>
    </div>
  );
}
